import React, { useState, useEffect, useMemo } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Building2, Search, Users, MapPin, ChevronDown, ChevronUp, Briefcase } from 'lucide-react';

export default function CorporateGroupsPage() {
  const api = useApi();
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get('/corporate-groups?limit=100').then(r => {
      setGroups(r.data.groups);
      setTotal(r.data.total_groups);
    }).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api]);

  const handleSearch = async (q) => {
    setSearch(q);
    if (q.length < 2) {
      api.get('/corporate-groups?limit=100').then(r => { setGroups(r.data.groups); setTotal(r.data.total_groups); }).catch(err => { console.error('API error:', err?.message); });
      return;
    }
    try {
      const { data } = await api.get(`/corporate-groups/search/${encodeURIComponent(q)}`);
      setGroups(data);
      setTotal(data.length);
    } catch (err) { console.error("API error:", err?.message); }
  };

  const summary = useMemo(() => {
    if (!groups.length) return { entities: 0, employees: 0, cities: 0 };
    return {
      entities: groups.reduce((s, g) => s + g.entity_count, 0),
      employees: groups.reduce((s, g) => s + (g.total_employees || 0), 0),
      cities: new Set(groups.flatMap(g => g.cities || [])).size,
    };
  }, [groups]);

  if (loading) return <div data-testid="groups-loading"><Skeleton className="h-64 rounded-sm" /></div>;

  return (
    <div className="space-y-6" data-testid="corporate-groups-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" style={{ color: '#0D1B2A', fontFamily: "'IBM Plex Sans'" }}>Corporate Groups</h1>
        <p className="text-sm mt-1" style={{ color: '#5C6B7A' }}>{total} groups | Entity relationship mapping</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Groups', value: total.toLocaleString(), icon: Building2, color: '#1A4C7C' },
          { label: 'Total Entities', value: summary.entities.toLocaleString(), icon: Briefcase, color: '#00B4D8' },
          { label: 'Locations', value: summary.cities, icon: MapPin, color: '#1D9E75' },
        ].map((kpi, i) => (
          <Card key={kpi.label} className="p-3 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
            <div className="flex items-center gap-2 mb-1">
              <kpi.icon className="w-3.5 h-3.5" style={{ color: kpi.color }} />
              <span className="text-[10px] uppercase tracking-wider" style={{ color: '#5C6B7A' }}>{kpi.label}</span>
            </div>
            <p className="text-lg font-bold font-mono" style={{ color: '#0D1B2A' }}>{kpi.value}</p>
          </Card>
        ))}
      </div>

      {/* Search */}
      <Card className="p-3 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#5C6B7A' }} />
          <Input data-testid="group-search" value={search} onChange={e => handleSearch(e.target.value)}
            placeholder="Search corporate groups..." className="pl-10 rounded-sm" />
        </div>
      </Card>

      {/* Group List */}
      <div className="space-y-2" data-testid="groups-list">
        {groups.map((g) => {
          const isOpen = expanded === g.group_id;
          return (
            <Card key={g.group_id} className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white overflow-hidden">
              <button onClick={() => setExpanded(isOpen ? null : g.group_id)}
                className="w-full p-4 flex items-center justify-between text-left hover:bg-[#F5F7FA] transition-colors"
                data-testid={`group-${g.group_name}`}>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-sm flex items-center justify-center shrink-0" style={{ background: 'rgba(26,76,124,0.08)' }}>
                    <Building2 className="w-5 h-5" style={{ color: '#1A4C7C' }} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold" style={{ color: '#0D1B2A' }}>{g.group_name}</span>
                      {g.country === 'NZ' && <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-medium" style={{ background: 'rgba(0,180,216,0.1)', color: '#00B4D8' }}>NZ</span>}
                    </div>
                    <div className="flex items-center gap-4 mt-0.5 text-[11px]" style={{ color: '#5C6B7A' }}>
                      <span>{g.entity_count.toLocaleString()} entities</span>
                      <span>{g.city_count} locations</span>
                      {g.sub_entity_types?.length > 0 && (
                        <span>{g.sub_entity_types.join(', ')}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right hidden md:block">
                    <p className="text-sm font-bold font-mono" style={{ color: '#0D1B2A' }}>{g.total_employees?.toLocaleString()}</p>
                    <p className="text-[10px]" style={{ color: '#5C6B7A' }}>total employees</p>
                  </div>
                  {isOpen ? <ChevronUp className="w-4 h-4" style={{ color: '#5C6B7A' }} /> : <ChevronDown className="w-4 h-4" style={{ color: '#5C6B7A' }} />}
                </div>
              </button>

              {isOpen && (
                <div className="px-4 pb-4 border-t border-[rgba(136,150,168,0.1)]">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                    <div className="p-2 rounded-sm" style={{ background: '#F5F7FA' }}>
                      <p className="text-[10px] uppercase" style={{ color: '#5C6B7A' }}>ABNs</p>
                      <p className="text-sm font-bold" style={{ color: '#0D1B2A' }}>{g.unique_abns?.toLocaleString()}</p>
                    </div>
                    <div className="p-2 rounded-sm" style={{ background: '#F5F7FA' }}>
                      <p className="text-[10px] uppercase" style={{ color: '#5C6B7A' }}>NZBNs</p>
                      <p className="text-sm font-bold" style={{ color: '#0D1B2A' }}>{g.unique_nzbns?.toLocaleString()}</p>
                    </div>
                    <div className="p-2 rounded-sm" style={{ background: '#F5F7FA' }}>
                      <p className="text-[10px] uppercase" style={{ color: '#5C6B7A' }}>Website</p>
                      <p className="text-sm font-bold truncate" style={{ color: '#1A4C7C' }}>{g.website || 'N/A'}</p>
                    </div>
                    <div className="p-2 rounded-sm" style={{ background: '#F5F7FA' }}>
                      <p className="text-[10px] uppercase" style={{ color: '#5C6B7A' }}>Industries</p>
                      <p className="text-sm font-bold" style={{ color: '#0D1B2A' }}>{g.industries?.length || 0}</p>
                    </div>
                  </div>
                  <div className="mt-3">
                    <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: '#5C6B7A' }}>Locations</p>
                    <div className="flex flex-wrap gap-1">
                      {(g.cities || []).map((city, ci) => (
                        <span key={ci} className="text-[10px] px-2 py-0.5 rounded-sm" style={{ background: '#F5F7FA', color: '#0D1B2A' }}>{city}</span>
                      ))}
                    </div>
                  </div>
                  {g.industries?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: '#5C6B7A' }}>Industries</p>
                      <div className="flex flex-wrap gap-1">
                        {g.industries.map((ind, ii) => (
                          <span key={ii} className="text-[10px] px-2 py-0.5 rounded-sm" style={{ background: 'rgba(0,180,216,0.06)', color: '#1A4C7C' }}>{ind}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  <p className="mt-3 text-[10px] italic" style={{ color: '#5C6B7A' }}>
                    Large corporate groups are flagged for manual underwriting. Individual entities within the group can be scored independently.
                  </p>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
